import type * as Y from 'yjs';

import { useEffect, useRef } from 'react';

import useWebsocket from './useWebsocket';

export const CONTENT_TYPE_DEFINITION_UPDATED_AT_KEY =
  'contentTypeDefinitionUpdatedAt';

const useWebsocketForForm = (
  contentTypeName?: string,
  id?: string,
  onVersionChange?: () => void,
  duplicate = false,
  onContentTypeDefinitionUpdate?: (updatedAt: string) => void,
) => {
  const observingRef = useRef(false);

  const { yjsDocument, wsStatus, retryWsConnection, editors, awareness } =
    useWebsocket(
      `${contentTypeName}/${id}`,
      !!contentTypeName && !duplicate && !!id,
    );

  const waitForYjsSync = !['synced', 'failed', 'idle', 'retrying'].includes(
    wsStatus,
  );

  useEffect(() => {
    if (observingRef.current || !yjsDocument) return;
    observingRef.current = true;

    const versionMap = yjsDocument.getMap('formState');

    const observeFn = (event: Y.YMapEvent<any>, transaction: Y.Transaction) => {
      if (transaction.local) return;

      if (event.keysChanged.has(CONTENT_TYPE_DEFINITION_UPDATED_AT_KEY)) {
        const updatedAt = versionMap.get(
          CONTENT_TYPE_DEFINITION_UPDATED_AT_KEY,
        );

        if (typeof updatedAt === 'string') {
          onContentTypeDefinitionUpdate?.(updatedAt);
        }

        return;
      }

      if (
        !event.keysChanged.has('latestVersion') &&
        !event.keysChanged.has('publicVersion')
      )
        return;

      if (onVersionChange) onVersionChange();
    };

    versionMap.observe(observeFn);
    return () => {
      observingRef.current = false;
      versionMap.unobserve(observeFn);
    };
  }, [onContentTypeDefinitionUpdate, onVersionChange, yjsDocument]);

  return {
    yjsDocument,
    wsStatus,
    retryWsConnection,
    editors,
    awareness,
    waitForYjsSync,
  };
};

export default useWebsocketForForm;
